import useIsContainerBeyondBreakpoint from '@/hooks/useIsContainerBeyondBreakpoint';
import AuthenticatedPageWrapper from '@/ui/layouts/authenticatedPageWrapper/AuthenticatedPageWrapper';
import { BreadcrumbItem, Button, Sidebar } from '@payaca/ambrosia';
import UntitledIcon from '@payaca/untitled-icons';
import { clstx } from '@payaca/utilities/cvaMerge';
import { FC, ReactNode, useCallback, useEffect, useState } from 'react';

type PreviewBuilderRenderProps = {
  isMobile: boolean;
  isPanelOpen: boolean;
  closePanel: () => void;
  openPanel: () => void;
};

export type PreviewBuilderLayoutProps = {
  // Forwarded to the AuthenticatedPageWrapper this layout renders itself within.
  breadcrumbItems?: BreadcrumbItem[];
  previewButtonLabel: ReactNode;
  renderEditor: (props: PreviewBuilderRenderProps) => ReactNode;
  renderPreview: (props: PreviewBuilderRenderProps) => ReactNode;
};

/**
 * A two-pane builder layout, rendered within an AuthenticatedPageWrapper.
 *
 * The editor and preview are supplied as render props. Each render prop
 * receives `isMobile` plus `closePanel` / `openPanel` callbacks so the
 * caller can react to the current presentation (e.g. close the preview sheet
 * after a tap on mobile, or open the collapsed editor panel on desktop).
 *
 * Desktop (md+): the preview fills the page and the editor is a panel that
 * slides in/out from the right. A chevron button toggles it.
 *
 *   ┌───────────────────────────────────────┬───────────────┐
 *   │                                     ▶ │               │
 *   │        ┌───────────────────┐          │    editor     │
 *   │        │                   │          │    sidebar    │
 *   │        │   preview pane    │          │    (panel)    │
 *   │        │  (centred, max    │          │               │
 *   │        │   900px, scrolls) │          │  renderEditor │
 *   │        └───────────────────┘          │               │
 *   │            renderPreview              │               │
 *   └───────────────────────────────────────┴───────────────┘
 *   Collapsed, the panel animates to width 0 and the chevron flips ◀.
 *
 * Mobile (< md): the editor fills the page with a button that opens the
 * preview as an ambrosia Sidebar sheet over the top.
 *
 *   ┌─────────────────────────┐        Sidebar sheet (isOpen)
 *   │                         │        ┌────────────────────────┐
 *   │     editor sidebar      │        │                        │
 *   │     renderEditor        │        │      preview pane      │
 *   │                         │   ──▶  │      renderPreview     │
 *   │                         │        │                        │
 *   ├─────────────────────────┤        │                        │
 *   │  [ previewButtonLabel ] │        └────────────────────────┘
 *   └─────────────────────────┘   The button toggles the sheet.
 */
const PreviewBuilderLayout: FC<PreviewBuilderLayoutProps> = (props) => {
  const { breadcrumbItems, previewButtonLabel, renderEditor, renderPreview } =
    props;

  const isMobile = !useIsContainerBeyondBreakpoint('md', {
    container: 'document',
  });
  const [isPanelOpen, setIsPanelOpen] = useState(!isMobile);

  const closePanel = useCallback(() => setIsPanelOpen(false), []);
  const openPanel = useCallback(() => setIsPanelOpen(true), []);

  // On mobile the preview is opened on demand via the button below; never let
  // it start open. The initial `!isMobile` can be momentarily stale before the
  // container is measured, which would otherwise pop the preview open over the
  // builder. Closing on the mobile transition leaves user-initiated opens
  // (which don't change `isMobile`) untouched.
  useEffect(() => {
    if (isMobile) setIsPanelOpen(false);
  }, [isMobile]);

  const content = isMobile ? (
    <div className="min-h-0 h-full flex flex-col">
      {renderEditor({ isMobile, isPanelOpen, closePanel, openPanel })}

      <div className="py-4 px-3.5 border-t">
        <Button
          className="w-full"
          variant="secondary"
          size="sm"
          onClick={() => setIsPanelOpen((open) => !open)}
        >
          {previewButtonLabel}
        </Button>
      </div>

      <Sidebar isOpen={isPanelOpen} onClose={() => setIsPanelOpen(false)}>
        <div className="flex-1 min-h-0 overflow-y-auto p-4 bg-gray-50">
          {renderPreview({ isMobile, isPanelOpen, closePanel, openPanel })}
        </div>
      </Sidebar>
    </div>
  ) : (
    // cqw - container query width
    // very cool css unit that allows you to get the width of a container query.
    // the `<main>` element from AuthenticatedPageWrapper is set as a `@container` query.
    // So --panel-w:min(50cqw,600px) = 50% width of the main container up to 600px
    // We can use this css variable later in our markup using [var(--panel-w)]
    // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Reads the panel width set on this layout
    <div className="relative flex min-h-0 h-full bg-gray-50 [--panel-w:min(50cqw,600px)]">
      <div className="min-w-0 flex-1 overflow-y-auto">
        <div className="p-8">
          <div className="max-w-6xl w-full mx-auto">
            {renderPreview({ isMobile, isPanelOpen, closePanel, openPanel })}
          </div>
        </div>
      </div>
      <div
        className={clstx(
          // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Limits the transition to these properties
          'absolute top-2 z-10 transition-[right] duration-300 ease-out',
          // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Reads the panel width set on this layout
          isPanelOpen ? 'right-[var(--panel-w)]' : 'right-0'
        )}
      >
        <Button
          className="rounded-r-none border-r-0"
          variant="secondary"
          aria-label={isPanelOpen ? 'Collapse preview' : 'Expand preview'}
          onClick={() => setIsPanelOpen((open) => !open)}
        >
          <UntitledIcon
            name={isPanelOpen ? 'chevron-right' : 'chevron-left'}
            className="size-4"
          />
        </Button>
      </div>
      <div
        inert={!isPanelOpen}
        aria-hidden={!isPanelOpen}
        className={clstx(
          // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Limits the transition to these properties
          'bg-white overflow-hidden border-l shrink-0 transition-[width] duration-300 ease-out',
          // oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Reads the panel width set on this layout
          isPanelOpen ? 'w-[var(--panel-w)]' : 'w-0'
        )}
      >
        {/* oxlint-disable-next-line payaca/no-tailwind-arbitrary-values -- Reads the panel width set on this layout */}
        <div className="h-full w-[var(--panel-w)]">
          {renderEditor({ isMobile, isPanelOpen, closePanel, openPanel })}
        </div>
      </div>
    </div>
  );

  return (
    <AuthenticatedPageWrapper breadcrumbItems={breadcrumbItems}>
      {content}
    </AuthenticatedPageWrapper>
  );
};

export default PreviewBuilderLayout;
